import {
  Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControlLabel, MenuItem, Stack, TextField
} from '@mui/material';
import { useUpdateLicenseMutation, type License } from '@store/api/licenseEndpoints';
import { serverMessage } from '@store/error';
import { useEffect, useState, type FormEvent } from 'react';
import { fieldFrom, secondsOf, type Unit, type ValueField } from './durationField';

// The same bounds the server enforces, so most mistakes are caught before the round trip. The
// server stays the authority: it also knows the ceiling and refuses anything above it.
const MIN_WINDOW = 3600;
const MAX_WINDOW = 14 * 86400;
const MIN_BUDGET = 15 * 60;

interface OfflineCreditDialogProps {
  license?: License;
  onClose: () => void;
}

/**
 * The administrator's own offline credit window and budget for one licence.
 *
 * The licence file's values are the ceiling. Each value here may be lower, zero included (zero in
 * either means no offline credit), or left to the file. The server issues the lower of the two.
 */
const OfflineCreditDialog = ({ license, onClose }: OfflineCreditDialogProps) => {
  const [updateLicense, { isLoading }] = useUpdateLicenseMutation();

  const [windowField, setWindowField] = useState<ValueField>(fieldFrom(undefined));
  const [budgetField, setBudgetField] = useState<ValueField>(fieldFrom(undefined));
  const [error, setError] = useState<string>();

  useEffect(() => {
    setWindowField(fieldFrom(license?.offlineCreditAdminWindowSeconds));
    setBudgetField(fieldFrom(license?.offlineCreditAdminBudgetSeconds));
    setError(undefined);
  }, [license]);

  const ceilingWindow = license?.offlineCreditWindowSeconds ?? 0;
  const ceilingBudget = license?.offlineCreditBudgetSeconds ?? 0;

  const windowSeconds = windowField.own ? secondsOf(windowField) : ceilingWindow;
  const budgetSeconds = budgetField.own ? secondsOf(budgetField) : ceilingBudget;

  const windowProblem = !windowField.own ? undefined
    : windowSeconds === undefined ? 'Enter 0 or more.'
      : windowSeconds !== 0 && (windowSeconds < MIN_WINDOW || windowSeconds > MAX_WINDOW)
        ? '0, or between 1 hour and 14 days.'
        : windowSeconds > ceilingWindow ? `The license file allows at most ${ceilingWindow.formatDuration()}.`
          : undefined;

  const budgetProblem = !budgetField.own ? undefined
    : budgetSeconds === undefined ? 'Enter 0 or more.'
      : budgetSeconds !== 0 && budgetSeconds < MIN_BUDGET ? '0, or at least 15 minutes.'
        : budgetSeconds > ceilingBudget ? `The license file allows at most ${ceilingBudget.formatDuration()}.`
          : undefined;

  // Checked only when both are nonzero: zero in either means no credit, whatever the other is.
  const pairProblem = !windowProblem && !budgetProblem && windowSeconds && budgetSeconds
    && budgetSeconds > windowSeconds
    ? 'The budget cannot be longer than the window.'
    : undefined;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!license) return;

    setError(undefined);

    try {
      await updateLicense({
        licenseId: license.licenseId,
        offlineCreditAdminWindowSeconds: windowField.own ? windowSeconds : null,
        offlineCreditAdminBudgetSeconds: budgetField.own ? budgetSeconds : null
      }).unwrap();

      onClose();
    } catch (caught) {
      setError(serverMessage(caught) ?? 'The offline credit could not be saved.');
    }
  };

  const valueRow = (
    label: string, field: ValueField, setField: (field: ValueField) => void,
    ceiling: number, problem?: string
  ) => (
    <Stack spacing={1}>
      <FormControlLabel
        control={<Checkbox checked={field.own} onChange={(event) => setField({ ...field, own: event.target.checked })} />}
        label={field.own ? `${label}, set here` : `${label}: the license file's ${ceiling.formatDuration()}`}
      />
      {field.own && (
        <Stack direction='row' spacing={1}>
          <TextField
            label={label}
            size='small'
            type='number'
            value={field.amount}
            onChange={(event) => setField({ ...field, amount: event.target.value })}
            error={!!problem}
            helperText={problem ?? `At most ${ceiling.formatDuration()}, the license file's value. 0 means no offline credit.`}
            slotProps={{ htmlInput: { min: 0, step: 'any' } }}
            fullWidth
          />
          <TextField
            select
            size='small'
            value={field.unit}
            onChange={(event) => setField({ ...field, unit: event.target.value as Unit })}
            sx={{ width: 100, flexShrink: 0 }}
          >
            <MenuItem value='min'>min</MenuItem>
            <MenuItem value='h'>hours</MenuItem>
            <MenuItem value='d'>days</MenuItem>
          </TextField>
        </Stack>
      )}
    </Stack>
  );

  return (
    <Dialog open={!!license} onClose={onClose} fullWidth maxWidth='sm'>
      <form onSubmit={onSubmit}>
        <DialogTitle>Offline credit</DialogTitle>

        <DialogContent>
          <DialogContentText>
            A device that loses contact may keep working for up to the <strong>window</strong> after
            its last seat, and for at most the <strong>budget</strong> of use in that time. The
            license file sets the most this license may have; you can set less here, or 0 for no
            offline credit. Changes reach each device at its next contact with this server. A device
            that is out of contact now keeps the allowance it already has.
          </DialogContentText>

          {/* The space above is the fields', not the text's: `index.css` zeroes every paragraph's
              bottom margin, so a margin on the text would never show. */}
          <Stack spacing={2} sx={{ mt: 2 }}>
            {/* The pair problem goes under whichever field is being edited: with the budget left
                at the file's value it has no input to show a message under, and Save would be
                disabled with nothing saying why. */}
            {valueRow('Window', windowField, setWindowField, ceilingWindow,
              windowProblem ?? (budgetField.own ? undefined : pairProblem))}
            {valueRow('Budget', budgetField, setBudgetField, ceilingBudget,
              budgetProblem ?? (budgetField.own ? pairProblem : undefined))}
          </Stack>

          {error && <Alert severity='error' sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type='submit'
            variant='contained'
            disabled={!license || isLoading || !!windowProblem || !!budgetProblem || !!pairProblem}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default OfflineCreditDialog;
